import { LayoutGroup } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { NavAccordionItem } from "@/components/layout/sidebar/NavAccordionItem";
import { NavItem } from "@/components/layout/sidebar/NavItem";
import { NavIcon } from "@/components/layout/sidebar/nav-icon";
import { SidebarFrame } from "@/components/layout/sidebar/SidebarFrame";
import type { INavItem } from "@/components/layout/sidebar/sidebar.types";
import { useSidebarMode } from "@/components/layout/sidebar/useSidebarMode";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  ISettingsNav,
  ISettingsNavGroup,
} from "@/config/settings-nav.config";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/utils/cn";
import { filterSettingsNav } from "./filter-settings-nav";
import { getAppReturnPath } from "./last-app-location";
import { useActiveSettingsItem } from "./use-active-settings-item";

const NO_IDS: ReadonlySet<number> = new Set();

interface ISettingsSidebarProps {
  nav: ISettingsNav;
  /** The app Layout's own sidebar state — what the top bar's toggle flips. */
  isSidebarOpen: boolean;
  isDesktop: boolean;
  onClose: () => void;
}

/**
 * The main menu, with the settings pages as its items.
 *
 * Same chrome (`SidebarFrame`), same rows and the same open/collapsed/overlay
 * behaviour as the app sidebar — driven by the app Layout's own sidebar
 * state, so the top bar's toggle works on it unchanged. What differs is only
 * the list: the predefined settings of the open space
 * (config/settings-nav.config.ts) instead of the permission-driven modules,
 * with a "← Settings" row in the space switcher's place leading back to the
 * app. A group is drawn as a module with page tabs is — an expandable
 * `NavAccordionItem` whose children are its pages, one open at a time, the
 * current page's opening by itself — and as a plain icon `NavItem` on the
 * collapsed rail.
 */
export const SettingsSidebar = ({
  nav,
  isSidebarOpen,
  isDesktop,
  onClose,
}: ISettingsSidebarProps) => {
  const { t } = useTranslation(["nav", "company"]);
  const { byDir } = useLocale();
  const { pathname, search: urlSearch } = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, isOverlay } = useSidebarMode(isSidebarOpen, isDesktop);
  const active = useActiveSettingsItem(nav);
  const returnPath = getAppReturnPath(
    nav.space === "company" ? "/company" : "/",
  );
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLUListElement>(null);

  const query = isCollapsed ? "" : search.trim();
  const groups = useMemo(
    () => filterSettingsNav(nav.groups, query, (key) => t(key)),
    [nav.groups, query, t],
  );
  // The rows key and track open state by a numeric id; number the groups
  // once, by label, so a filtered copy of a group keeps its id. A group row
  // links to its section's first page — whatever the filter left showing.
  const groupMeta = useMemo(
    () =>
      new Map(
        nav.groups.map((g, i) => [g.label, { id: i + 1, to: g.items[0].to }]),
      ),
    [nav.groups],
  );

  // Opened from the first render, not by the row's auto-expand effect a
  // moment later: content that is open at mount doesn't animate, so arriving
  // on Settings shows the current group open instead of sliding it open.
  const [openGroupId, setOpenGroupId] = useState<number | null>(() =>
    active.group ? (groupMeta.get(active.group.label)?.id ?? null) : null,
  );

  // While filtering, every group with a match starts open so the matches
  // show — but its chevron still works. What the user closed is remembered
  // for this query only: the next keystroke is a new result set.
  const [closedWhileFiltering, setClosedWhileFiltering] = useState<{
    query: string;
    ids: ReadonlySet<number>;
  }>({ query: "", ids: NO_IDS });
  const filterClosedIds =
    closedWhileFiltering.query === query ? closedWhileFiltering.ids : NO_IDS;

  // One function for every row, and a new one only when the query changes:
  // `NavAccordionItem`'s auto-expand effect depends on its identity (see the
  // main `Sidebar`'s `handleAccordionOpenChange`, which this mirrors).
  const handleGroupOpenChange = useCallback(
    (id: number, open: boolean) => {
      if (query) {
        setClosedWhileFiltering((prev) => {
          const ids = new Set(prev.query === query ? prev.ids : NO_IDS);
          if (open) ids.delete(id);
          else ids.add(id);
          return { query, ids };
        });
        return;
      }
      setOpenGroupId((current) => {
        if (open) return id;
        return current === id ? null : current;
      });
    },
    [query],
  );

  // As the main menu does: an overlay closes itself once you pick a page.
  const handleLinkClick = () => {
    setSearch("");
    if (isOverlay) onClose();
  };

  // The filter's keyboard: Enter opens the first match, ArrowDown steps into
  // the list, Escape clears a filled field — and stops there, so that press
  // doesn't also close the overlay (the next Escape does).
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const first = groups[0]?.items[0];
      if (!query || !first) return;
      e.preventDefault();
      navigate(first.to);
      handleLinkClick();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      listRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    } else if (e.key === "Escape" && search) {
      e.stopPropagation();
      setSearch("");
    }
  };

  // Up/Down walk the list's links; Tab still works too.
  const handleListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const links = Array.from(e.currentTarget.querySelectorAll("a"));
    const index = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (index === -1) return;
    e.preventDefault();
    const step = e.key === "ArrowDown" ? 1 : -1;
    links[(index + step + links.length) % links.length]?.focus();
  };

  const toNavItem = (group: ISettingsNavGroup): INavItem => {
    const meta = groupMeta.get(group.label);
    return {
      id: meta?.id ?? 0,
      name: t(group.label),
      url: meta?.to ?? group.items[0].to,
      icon: <NavIcon src={group.iconSrc} />,
      categoryId: null,
    };
  };

  const backLabel = t("nav:settings_nav.back_to_app");
  const backLink = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground hover:bg-primary/10 hover:text-primary lg:size-10"
        >
          <Link
            to={returnPath}
            onClick={handleLinkClick}
            aria-label={backLabel}
          >
            <ArrowLeft className="size-4 rtl-flip" aria-hidden />
          </Link>
        </Button>
      </TooltipTrigger>
      <TooltipContent side={isCollapsed ? byDir("right", "left") : "bottom"}>
        {backLabel}
      </TooltipContent>
    </Tooltip>
  );

  return (
    // Namespaces NavItem's sliding-highlight layoutIds: the main menu stays
    // mounted (hidden) beside this one, and a shared id would animate the
    // highlight across from it.
    <LayoutGroup id="settings-sidebar">
      <SidebarFrame
        isSidebarOpen={isSidebarOpen}
        isCollapsed={isCollapsed}
        isOverlay={isOverlay}
        onClose={onClose}
        label={t("nav:modules.settings")}
        navLabel={t("nav:settings_nav.navigation")}
        header={
          // In the space switcher's place: the way back, and where you are.
          isCollapsed ? (
            <div className="flex shrink-0 justify-center border-b border-border px-1.5 py-2">
              {backLink}
            </div>
          ) : (
            <div className="flex shrink-0 items-center gap-1.5 px-2 pt-2 lg:px-3 lg:pt-3">
              {backLink}
              <h2 className="min-w-0 truncate text-sm font-semibold text-foreground lg:text-base">
                {t("nav:modules.settings")}
              </h2>
            </div>
          )
        }
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t("nav:settings_nav.search_placeholder"),
          onKeyDown: handleSearchKeyDown,
        }}
      >
        <ul
          ref={listRef}
          onKeyDown={handleListKeyDown}
          className={cn(
            "flex flex-col gap-0.5 lg:gap-1",
            isCollapsed ? "p-1.5 lg:p-2" : "p-2 lg:p-3",
          )}
        >
          {groups.map((group) => {
            const item = toNavItem(group);
            const isGroupActive = group.label === active.group?.label;
            return (
              <li key={group.label}>
                {isCollapsed ? (
                  <NavItem
                    item={item}
                    isCollapsed
                    currentPath={pathname}
                    onLinkClick={handleLinkClick}
                    active={isGroupActive}
                  />
                ) : (
                  <NavAccordionItem
                    item={item}
                    tabs={group.items.map(({ label, to }) => ({ label, to }))}
                    translateLabel={(key) => t(key)}
                    currentPath={pathname}
                    currentSearch={urlSearch}
                    onLinkClick={handleLinkClick}
                    isOpen={
                      query
                        ? !filterClosedIds.has(item.id)
                        : openGroupId === item.id
                    }
                    onOpenChange={handleGroupOpenChange}
                    active={isGroupActive}
                    activeTab={active.item?.to ?? null}
                  />
                )}
              </li>
            );
          })}
          {query && groups.length === 0 && (
            <li className="px-2 py-6 text-center text-xs text-muted-foreground">
              {t("nav:settings_nav.no_results", { query })}
            </li>
          )}
        </ul>
      </SidebarFrame>
    </LayoutGroup>
  );
};
